import { ChangeEvent, DragEvent, useMemo, useState } from 'react'

import { getBulkRejectRowErrors } from './getBulkRejectRowErrors'
import { BULK_REJECT_MAX_ROWS, ParsedBulkRejectCsv, parseBulkRejectCsv } from './parseBulkRejectCsv'
import { BulkRejectException, CHECK_FAILED_OUTCOME, verifyBulkRejectOutcomes } from './verifyBulkRejectOutcomes'
import { getApiErrorCode } from '@percent/admin-dashboard/common/library/getApiErrorCode'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/utility'

export type BulkRejectView = 'upload' | 'confirm' | 'submitting' | 'submitted' | 'verifying' | 'verified' | 'error'

type UseAgentVerificationBulkRejectProps = {
  onClose: () => void
}

export function useAgentVerificationBulkReject({ onClose }: UseAgentVerificationBulkRejectProps) {
  const { agentVerificationService } = useServices()
  const [view, setView] = useState<BulkRejectView>('upload')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [hasTooManyFilesError, setHasTooManyFilesError] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [parsed, setParsed] = useState<ParsedBulkRejectCsv | null>(null)
  const [rejectedCount, setRejectedCount] = useState(0)
  const [checkedCount, setCheckedCount] = useState(0)
  const [exceptions, setExceptions] = useState<BulkRejectException[]>([])

  const [{ data: submitData, error: submitError }, { mutateAsync: submitBulkReject }] = useMutation(
    agentVerificationService.bulkRejectAgentVerifications
  )

  const close = () => {
    setView('upload')
    setSelectedFiles([])
    setHasTooManyFilesError(false)
    setCsvText('')
    setParsed(null)
    setRejectedCount(0)
    setCheckedCount(0)
    setExceptions([])
    onClose()
  }

  const selectFiles = (files: File[]) => {
    setSelectedFiles(files)
    setHasTooManyFilesError(false)
  }

  const captureFileInputChange = (event: ChangeEvent<HTMLDivElement>) => {
    const { target } = event

    if (!(target instanceof HTMLInputElement) || target.type !== 'file') return

    setHasTooManyFilesError((target.files?.length ?? 0) > 1)
  }

  const captureDrop = (event: DragEvent<HTMLDivElement>) => {
    setHasTooManyFilesError((event.dataTransfer?.files?.length ?? 0) > 1)
  }

  const review = async () => {
    const csv = await selectedFiles[0].text()

    setCsvText(csv)
    setParsed(parseBulkRejectCsv(csv))
    setView('confirm')
  }

  const submit = async () => {
    setView('submitting')

    try {
      await submitBulkReject(csvText)
      setView('submitted')
    } catch {
      setView('error')
    }
  }

  const verify = async () => {
    setView('verifying')
    setCheckedCount(0)

    try {
      const result = await verifyBulkRejectOutcomes({
        agentVerificationIds: parsed?.rows.map(row => row.agentVerificationId) ?? [],
        fetchAgentVerification: agentVerificationService.getAgentVerification,
        onProgress: setCheckedCount
      })

      setRejectedCount(result.rejectedCount)
      setExceptions(result.exceptions)
      setView('verified')
    } catch {
      setView('submitted')
    }
  }

  const rowErrors = useMemo(() => getBulkRejectRowErrors(submitError), [submitError])

  // Rows we could not read back are counted apart from rows the queue genuinely left unrejected.
  const checkFailedCount = exceptions.filter(({ outcome }) => outcome === CHECK_FAILED_OUTCOME).length

  const rowCount = parsed?.rows.length ?? 0
  const hasMissingHeaders = (parsed?.missingHeaders.length ?? 0) > 0

  return {
    view,
    selectedFiles,
    parsed,
    rowCount,
    hasMissingHeaders,
    duplicateIds: parsed?.duplicateIds ?? [],
    canReview: selectedFiles.length > 0 && !hasTooManyFilesError,
    canSubmit: rowCount > 0 && rowCount <= BULK_REJECT_MAX_ROWS && !hasMissingHeaders,
    batchId: submitData?.data.batchId ?? '',
    queuedCount: submitData?.data.total ?? rowCount,
    errorCode: getApiErrorCode(submitError),
    rowErrors,
    rejectedCount,
    checkedCount,
    exceptions,
    checkFailedCount,
    close,
    selectFiles,
    captureFileInputChange,
    captureDrop,
    review,
    submit,
    verify
  }
}
